/* SpawnAnything — Schlacht per Link (Phase D1, variant "Nachspielen", 02.10.2026): the setup of a battle (both sides'
   troops, painted placement, buildings, map, look, world options, own AI figures) travels in the URL fragment
   "#b=<base64url(deflate-raw(JSON))>" (never sent to the server). Whoever opens the link sees the battle set up, can tip
   the winner and watch, or change it first. Pure pack/unpack/sanitize + a small UI; main.js passes `host`:
     host: { snapshot() -> setup, apply(clean), start(), toast(msg), blocked() -> '' | reason,
             types, weapons, buildings (built-in keys), styles, terrains, validateRecipe(rec) -> rec | null } */

export const LINK_VERSION = 1;
const MAX_UNITS = 6000, MAX_POS = 4000, MAX_BLD = 60, MAX_RECIPES = 4, ARENA = 230, Q = 4; // positions in 1/4 m
const esc = s => String(s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);

/* ---------------- encoding ---------------- */
const b64url = bytes => { let s = ''; for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000)); return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, ''); };
const unb64url = str => { const s = atob(str.replace(/-/g, '+').replace(/_/g, '/')); const b = new Uint8Array(s.length); for (let i = 0; i < s.length; i++) b[i] = s.charCodeAt(i); return b; };
async function pipe(bytes, stream) { return new Uint8Array(await new Response(new Blob([bytes]).stream().pipeThrough(stream)).arrayBuffer()); }

/** setup object -> fragment payload (without "#b=") */
export async function packSetup(o) {
  const raw = new TextEncoder().encode(JSON.stringify(o));
  return b64url(await pipe(raw, new CompressionStream('deflate-raw')));
}
/** fragment payload -> setup object, or null */
export async function unpackSetup(str) {
  try {
    if (typeof str !== 'string' || str.length > 200000 || !/^[A-Za-z0-9_-]+$/.test(str)) return null;
    const raw = await pipe(unb64url(str), new DecompressionStream('deflate-raw'));
    if (raw.length > 2_000_000) return null;
    return JSON.parse(new TextDecoder().decode(raw));
  } catch { return null; }
}

/* ---------------- setup <-> compact object ---------------- */
const r2 = v => Math.round(v * 100);
/** host snapshot -> compact link object */
export function compactSetup(s) {
  const o = { v: LINK_VERSION, g: s.groups.map(G => [G.team, G.slot | 0, G.type, G.weapon, r2(G.size || 1), r2(G.t ?? 0.4), r2(G.width ?? 1), r2(G.head ?? 1), G.mount || '', G.count | 0]) };
  if (s.mode === 'paint') {
    o.p = [0, 1].map(t => Array.from(s.paint[t], v => Math.round(v * Q)));
    o.s = [0, 1].map(t => Array.from({ length: s.paint[t].length / 2 }, (_, k) => s.paintS[t][k] | 0));
  }
  if (s.buildings.length) o.b = s.buildings.map(b => [b.key, b.team, b.level, Math.round(b.x * 10), Math.round(b.z * 10), b.rot | 0]);
  o.w = [s.terrain, s.seed | 0, s.style, s.world.weather || 'auto', s.world.crater ? 1 : 0, s.world.infection ? 1 : 0, s.world.conversion ? 1 : 0];
  if (s.recipes && Object.keys(s.recipes).length) o.r = s.recipes; // own AI figures: type key -> recipe
  return o;
}

/** compact link object -> clean setup (everything checked against what this game knows), or null */
export function sanitizeSetup(o, k) {
  if (!o || typeof o !== 'object' || o.v !== LINK_VERSION || !Array.isArray(o.g)) return null;
  const num = (v, lo, hi, d = lo) => (Number.isFinite(+v) ? Math.min(hi, Math.max(lo, +v)) : d);
  const recipes = {};
  if (o.r && typeof o.r === 'object') {
    for (const [key, rec] of Object.entries(o.r).slice(0, MAX_RECIPES)) {
      if (!/^u_[A-Za-z0-9_]{1,40}$/.test(key)) continue;
      const v = k.validateRecipe(rec); if (v) recipes[key] = v;
    }
  }
  const groups = [], seen = new Set();
  let units = 0;
  for (const g of o.g.slice(0, 8)) {
    if (!Array.isArray(g)) continue;
    const team = g[0] === 1 ? 1 : 0, slot = num(g[1], 0, 3) | 0, id = team * 4 + slot;
    if (seen.has(id)) continue;
    const type = String(g[2]); if (!k.types[type] && !recipes[type]) continue;
    const weapon = k.weapons[g[3]] ? String(g[3]) : null;
    const count = num(g[9], 0, MAX_UNITS) | 0; units += count; if (units > MAX_UNITS) return null;
    seen.add(id);
    groups.push({ team, slot, type, weapon, size: num(g[4], 25, 2000, 100) / 100, t: num(g[5], 0, 100, 40) / 100, width: num(g[6], 30, 300, 100) / 100, head: num(g[7], 30, 300, 100) / 100, mount: typeof g[8] === 'string' && k.types[g[8]] ? g[8] : '', count });
  }
  if (!groups.some(G => G.team === 0) || !groups.some(G => G.team === 1)) return null;
  const out = { groups, recipes, mode: 'count', paint: [[], []], paintS: [[], []], buildings: [] };
  if (Array.isArray(o.p) && Array.isArray(o.s)) {
    out.mode = 'paint';
    for (let t = 0; t < 2; t++) {
      const p = Array.isArray(o.p[t]) ? o.p[t] : [], s = Array.isArray(o.s[t]) ? o.s[t] : [], slots = new Set(groups.filter(G => G.team === t).map(G => G.slot));
      for (let i = 0; i < Math.min(p.length / 2, s.length, MAX_POS); i++) {
        const x = +p[2 * i] / Q, z = +p[2 * i + 1] / Q, sl = s[i] | 0;
        if (!Number.isFinite(x) || !Number.isFinite(z) || Math.abs(x) > ARENA || Math.abs(z) > ARENA || !slots.has(sl)) continue;
        out.paint[t].push(x, z); out.paintS[t].push(sl);
      }
    }
  }
  if (Array.isArray(o.b)) for (const b of o.b.slice(0, MAX_BLD)) {
    if (!Array.isArray(b) || !k.buildings.includes(b[0])) continue;
    out.buildings.push({ key: b[0], team: b[1] === 1 ? 1 : 0, level: num(b[2], 1, 3, 1) | 0, x: num(b[3], -ARENA * 10, ARENA * 10, 0) / 10, z: num(b[4], -ARENA * 10, ARENA * 10, 0) / 10, rot: num(b[5], 0, 3, 0) | 0 });
  }
  const w = Array.isArray(o.w) ? o.w : [];
  out.terrain = k.terrains.includes(w[0]) ? w[0] : 'flach';
  out.seed = num(w[1], 0, 2 ** 31, 0) | 0;
  out.style = k.styles.includes(w[2]) ? w[2] : null;
  out.world = { weather: typeof w[3] === 'string' && /^[a-z_]{1,20}$/.test(w[3]) ? w[3] : 'auto', crater: !!w[4], infection: !!w[5], conversion: !!w[6] };
  return out;
}

/* ---------------- UI ---------------- */
export function createBattleLink(host) {
  let tip = -1;
  const card = document.createElement('section');
  card.className = 'dice-card link-card'; card.hidden = true; card.setAttribute('role', 'dialog'); card.setAttribute('aria-label', 'Geteilte Schlacht');
  document.body.appendChild(card);
  const sideText = (s, t) => s.groups.filter(G => G.team === t).map(G => `${(G.count || 0).toLocaleString('de-DE')} ${host.typeName(G.type, G.count)}`).join(' + ');

  /** the link of the current setup: { url, note } */
  async function link() {
    const o = compactSetup(host.snapshot());
    let note = '';
    if (o.p && o.p[0].length + o.p[1].length > MAX_POS * 2) { delete o.p; delete o.s; note = ' (zu viele einzeln gesetzte Einheiten: der Link stellt nach Anzahl auf)'; }
    return { url: `${location.origin}${location.pathname}#b=${await packSetup(o)}`, note };
  }
  async function share() {
    const why = host.blocked(); if (why) { host.toast(why); return; }
    let url, note;
    try { ({ url, note } = await link()); } catch { host.toast('Dieser Browser kann keinen Link erzeugen.'); return; }
    const title = 'SpawnAnything: Wer gewinnt?';
    try {
      if (navigator.share && matchMedia('(pointer: coarse)').matches) { await navigator.share({ title, text: 'Tipp, wer gewinnt, oder spiel die Schlacht nach:', url }); return; }
    } catch (e) { if (e && e.name === 'AbortError') return; }
    try { await navigator.clipboard.writeText(url); host.toast('Link kopiert: schick ihn an Freunde' + note); }
    catch { window.prompt('Link zum Kopieren:', url); }
  }

  /** "#b=…" in the address: set the battle up and ask for a tip. true when a link was found. */
  async function openFromHash() {
    const m = /^#b=([A-Za-z0-9_-]+)$/.exec(location.hash || '');
    if (!m) return false;
    try { history.replaceState(null, '', location.pathname + location.search); } catch { /* keep the hash */ }
    const s = sanitizeSetup(await unpackSetup(m[1]), host);
    if (!s) { host.toast('Der Schlacht-Link ist kaputt oder zu alt.'); return true; }
    host.apply(s);
    tip = -1;
    card.innerHTML = `<div class="dice-h"><i class="ti ti-link" aria-hidden="true"></i> Geteilte Schlacht: wer gewinnt?</div>
      <div class="dice-vs"><b class="t0">${esc(sideText(s, 0))}</b><span>gegen</span><b class="t1">${esc(sideText(s, 1))}</b></div>
      <div class="dice-pick"><button class="tb0" data-tip="0">Blau gewinnt</button><button class="tb1" data-tip="1">Rot gewinnt</button></div>
      <div class="dice-foot"><button class="ghost" data-a="watch"><i class="ti ti-player-play" aria-hidden="true"></i> Einfach ansehen</button><button class="ghost" data-a="edit">Erst ändern</button></div>`;
    card.hidden = false; card.querySelector('[data-tip]')?.focus();
    return true;
  }
  card.addEventListener('click', e => {
    const t = e.target.closest('[data-tip]'), a = e.target.closest('[data-a]');
    if (t) { tip = +t.dataset.tip; card.hidden = true; host.start(); }
    else if (a && a.dataset.a === 'watch') { tip = -1; card.hidden = true; host.start(); }
    else if (a && a.dataset.a === 'edit') { tip = -1; card.hidden = true; host.toast('Ändere die Aufstellung und starte, wenn du so weit bist.'); }
  });
  /** battle over: line for the victory banner ('' without a tip) */
  function finish(winner) {
    if (tip < 0) return '';
    const ok = winner === tip; tip = -1;
    return ok ? 'Dein Tipp war richtig!' : 'Dein Tipp lag daneben.';
  }
  return { share, link, openFromHash, finish, close: () => { card.hidden = true; } };
}
